import { SubsystemSelectDto, UserProfileRole } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component.tsx';
import { useGetTechnology } from '@queries/technology.query.ts';
import { useGetCurrentUser } from '@queries/users.query.ts';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { FunctionComponent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SubsystemItem } from './subsystem-item.component.tsx';
import { TechnologySubsystemModal } from './technology-subsystem-modal.component.tsx';

export const TechnologySubsystems: FunctionComponent = () => {
  const { technologyId } = useParams();
  const { data: technology, isPending: isTechnologyLoading } = useGetTechnology(technologyId);
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = user?.profile?.role === UserProfileRole.INNOVATOR && !isChildOrganizationView;

  const [addSubsystemModalOpen, setAddSubsystemModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [subsystemToEdit, setSubsystemToEdit] = useState<SubsystemSelectDto | null>(null);

  const handleOpenModal = (subsystem?: SubsystemSelectDto) => {
    setIsEdit(!!subsystem);
    setSubsystemToEdit(subsystem || null);
    setAddSubsystemModalOpen(true);
  };

  const isEmpty = !technology?.subsystems?.length;

  const headerButton = { onClick: handleOpenModal, text: '+ Add' };

  return (
    <>
      <Tile
        className="min-h-[182px]"
        emptyState={{
          message: canEdit ? 'Add Subsystem' : undefined,
          onClick: canEdit ? () => setAddSubsystemModalOpen(true) : undefined,
          title: 'No Subsystems',
        }}
        header={{ button: canEdit ? headerButton : undefined, title: 'Subsystems' }}
        isEmpty={isEmpty}
        isLoading={isTechnologyLoading}
      >
        {technology?.subsystems?.length && (
          <div className="flex flex-col gap-4">
            {technology.subsystems.map((subsystem) => (
              <SubsystemItem
                subsystem={subsystem}
                key={subsystem.id}
                onEdit={canEdit ? () => handleOpenModal(subsystem) : undefined}
              />
            ))}
          </div>
        )}
      </Tile>
      {technology && canEdit && (
        <TechnologySubsystemModal
          technology={technology}
          isOpen={addSubsystemModalOpen}
          setIsOpen={setAddSubsystemModalOpen}
          isEdit={isEdit}
          subsystem={subsystemToEdit}
        />
      )}
    </>
  );
};
